/**
 * Tenant Service
 *
 * Provides utilities for tenant validation, sanitization, and consistency checking.
 * Handles security validation between subdomain and query parameter.
 */

// Reserved subdomains that are NOT tenant subdomains (platform domains)
const RESERVED_SUBDOMAINS = [
  'api', // Production API: api.llamasee.com
  'dev-api', // Development API: dev-api.llamasee.com
  'staging-api', // Staging API: staging-api.llamasee.com
  'admin', // Admin interface: admin.llamasee.com
  'www', // Main website: www.llamasee.com
  'oauth', // OAuth service: oauth.llamasee.com
  'dev', // Development frontend: dev.llamasee.com
  'staging', // Staging frontend: staging.llamasee.com
  'admapi', // Admin API: admapi.llamasee.com
  'dev-admapi', // Dev Admin API: dev-admapi.llamasee.com
  'test', // Test environment: test.llamasee.com
];

/**
 * Check if a subdomain is reserved (platform subdomain, not tenant subdomain)
 * @param {string} subdomain - The subdomain to check
 * @returns {boolean} True if subdomain is reserved
 */
export const isReservedSubdomain = subdomain => {
  if (!subdomain) return false;
  return RESERVED_SUBDOMAINS.includes(subdomain.toLowerCase());
};

/**
 * Extract tenant from subdomain (if tenant subdomain, not reserved)
 * @param {string} hostname - The hostname (e.g., 'demo.llamasee.com')
 * @returns {string|null} Tenant alias or null if not a tenant subdomain
 */
export const extractTenantFromSubdomain = hostname => {
  if (!hostname || typeof hostname !== 'string') return null;

  // localhost/127.0.0.1 are not subdomains
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  const parts = hostname.split('.');

  // Check if it's a llamasee.com subdomain
  if (parts.length >= 3 && parts[1] === 'llamasee' && parts[2] === 'com') {
    const subdomain = parts[0];

    // Skip reserved subdomains
    if (isReservedSubdomain(subdomain)) {
      return null;
    }

    // Return tenant subdomain
    return subdomain;
  }

  return null;
};

/**
 * Extract tenant from query parameter
 * @param {string} search - URL search string (e.g., '?tenant=demo')
 * @returns {string|null} Tenant alias or null
 */
export const extractTenantFromQueryParam = search => {
  if (!search) return null;
  try {
    const urlParams = new URLSearchParams(search);
    return urlParams.get('tenant') || null;
  } catch (error) {
    return null;
  }
};

/**
 * Validate tenant format
 * @param {string} tenant - Tenant alias to validate
 * @returns {{valid: boolean, error?: string, tenant?: string}} Validation result
 */
export const validateTenantFormat = tenant => {
  if (!tenant || typeof tenant !== 'string') {
    return { valid: false, error: 'Tenant alias is required' };
  }

  // Format: Alphanumeric, underscores, hyphens only
  const formatRegex = /^[a-zA-Z0-9_-]{3,50}$/;
  if (!formatRegex.test(tenant)) {
    return {
      valid: false,
      error:
        'Invalid tenant format. Must be 3-50 characters, alphanumeric, underscores, or hyphens only.',
    };
  }

  // Check if it's a reserved name
  if (isReservedSubdomain(tenant)) {
    return {
      valid: false,
      error: `'${tenant}' is a reserved subdomain and cannot be used as a tenant alias`,
    };
  }

  return { valid: true, tenant: tenant.trim() };
};

/**
 * Sanitize tenant input
 * @param {string} tenant - Tenant alias to sanitize
 * @returns {string} Sanitized tenant alias
 */
export const sanitizeTenant = tenant => {
  if (!tenant || typeof tenant !== 'string') return '';

  // Remove invalid characters, keep only alphanumeric, underscores, hyphens
  return tenant.replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 50);
};

/**
 * Validate tenant consistency between subdomain and query parameter
 * SECURITY: Subdomain is authoritative when present (tenant subdomain)
 * @param {string|null} tenantFromSubdomain - Tenant from subdomain
 * @param {string|null} tenantFromQuery - Tenant from query parameter
 * @param {object|null} tenantFromStorage - Tenant from localStorage
 * @returns {{tenant: string|null, warning?: string}} Validated tenant and optional warning
 */
export const validateTenantConsistency = (
  tenantFromSubdomain,
  tenantFromQuery,
  tenantFromStorage = null
) => {
  // Extract alias from storage object if it's an object
  const storageAlias = tenantFromStorage
    ? typeof tenantFromStorage === 'string'
      ? tenantFromStorage
      : tenantFromStorage.alias || tenantFromStorage.tenant_id || null
    : null;

  // If subdomain exists and is a tenant subdomain (not reserved), it's authoritative
  if (tenantFromSubdomain) {
    // Validate query parameter matches subdomain
    if (tenantFromQuery && tenantFromQuery !== tenantFromSubdomain) {
      const warning = `Security: Query parameter tenant '${tenantFromQuery}' does not match subdomain '${tenantFromSubdomain}'. Using subdomain tenant.`;
      return { tenant: tenantFromSubdomain, warning };
    }

    // Validate storage tenant matches subdomain
    if (storageAlias && storageAlias !== tenantFromSubdomain) {
      const warning = `Security: Stored tenant '${storageAlias}' does not match subdomain '${tenantFromSubdomain}'. Using subdomain tenant.`;
      return { tenant: tenantFromSubdomain, warning };
    }

    // Subdomain is authoritative, use it
    return { tenant: tenantFromSubdomain };
  }

  // No subdomain (or reserved subdomain) - use query param or storage
  if (tenantFromQuery) {
    return { tenant: tenantFromQuery };
  }

  if (storageAlias) {
    return { tenant: storageAlias };
  }

  return { tenant: null };
};

/**
 * Get tenant alias from URL with security validation
 * Priority: Subdomain (if tenant subdomain) > Query Param > Storage
 * @param {string} hostname - Current hostname
 * @param {string} search - URL search string
 * @param {object|null} storedTenant - Tenant from localStorage
 * @returns {{tenant: string|null, warning?: string}} Validated tenant
 */
export const getTenantFromUrl = (hostname, search, storedTenant = null) => {
  const tenantFromSubdomain = extractTenantFromSubdomain(hostname);
  const tenantFromQuery = extractTenantFromQueryParam(search);

  return validateTenantConsistency(
    tenantFromSubdomain,
    tenantFromQuery,
    storedTenant
  );
};

const tenantService = {
  isReservedSubdomain,
  extractTenantFromSubdomain,
  extractTenantFromQueryParam,
  validateTenantFormat,
  sanitizeTenant,
  validateTenantConsistency,
  getTenantFromUrl,
};

export default tenantService;
